import { useMemo, useState } from "react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function LoanCalculator({ defaultAmount = 2000, defaultTerm = 12, defaultRate = 18 }) {
  const [amount, setAmount] = useState(defaultAmount);
  const [term, setTerm] = useState(defaultTerm);
  const [rate, setRate] = useState(defaultRate);

  const { monthly, total, interest } = useMemo(() => {
    const r = rate / 100 / 12;
    const n = term;
    const emi = r === 0 ? amount / n : (amount * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
    const tot = emi * n;
    return { monthly: emi, total: tot, interest: tot - amount };
  }, [amount, term, rate]);

  const fmt = (v) => `$${Number(v || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;

  return (
    <div className="overflow-hidden rounded-2xl border border-[#E5E2DE] bg-white shadow-sm" data-testid="loan-calculator">
      <div className="grid gap-0 md:grid-cols-5">
        <div className="space-y-6 p-6 md:col-span-3 md:p-8">
          <div>
            <div className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#E87722]">
              Loan Calculator
            </div>
            <h3 className="font-heading text-2xl font-semibold text-ink">Estimate your repayments</h3>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-sm font-medium text-ink-soft">Loan amount</Label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(Math.max(0, Number(e.target.value) || 0))}
                className="h-9 w-32 text-right"
                data-testid="calc-amount-input"
              />
            </div>
            <Slider
              value={[amount]}
              onValueChange={(v) => setAmount(v[0])}
              min={100}
              max={50000}
              step={100}
              data-testid="calc-amount-slider"
            />
            <div className="mt-1 flex justify-between text-xs text-ink-muted">
              <span>$100</span><span>$50,000</span>
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-sm font-medium text-ink-soft">Term (months)</Label>
              <Input
                type="number"
                value={term}
                onChange={(e) => setTerm(Math.max(1, Math.min(60, Number(e.target.value) || 0)))}
                className="h-9 w-32 text-right"
                data-testid="calc-term-input"
              />
            </div>
            <Slider value={[term]} onValueChange={(v) => setTerm(v[0])} min={1} max={48} step={1} data-testid="calc-term-slider" />
            <div className="mt-1 flex justify-between text-xs text-ink-muted">
              <span>1 mo</span><span>48 mo</span>
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label className="text-sm font-medium text-ink-soft">Annual interest rate</Label>
              <Input
                type="number"
                value={rate}
                onChange={(e) => setRate(Math.max(0, Number(e.target.value) || 0))}
                className="h-9 w-32 text-right"
                data-testid="calc-rate-input"
              />
            </div>
            <Slider value={[rate]} onValueChange={(v) => setRate(v[0])} min={5} max={40} step={1} data-testid="calc-rate-slider" />
            <div className="mt-1 flex justify-between text-xs text-ink-muted">
              <span>5%</span><span>40%</span>
            </div>
          </div>
        </div>

        <div className="border-t border-[#E5E2DE] bg-[#5B2C82] p-6 text-white md:col-span-2 md:border-l md:border-t-0 md:p-8">
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-[#E87722]">Your estimate</div>
          <div className="mt-4 space-y-5">
            <div>
              <div className="text-sm text-white/70">Monthly payment</div>
              <div className="font-heading text-4xl font-bold" data-testid="calc-monthly">{fmt(monthly)}</div>
            </div>
            <div className="border-t border-white/10 pt-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-white/70">Total interest</span>
                <span className="font-semibold" data-testid="calc-interest">{fmt(interest)}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-sm">
                <span className="text-white/70">Total repayable</span>
                <span className="font-semibold" data-testid="calc-total">{fmt(total)}</span>
              </div>
            </div>
            <p className="border-t border-white/10 pt-4 text-xs leading-relaxed text-white/60">
              Indicative only. Final rates and terms depend on credit assessment, customer
              segment, and product chosen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
